import { Theme } from "@emotion/react";
import { SxProps } from "@mui/material";

export interface Book {
  id: number;
  name: string;
  isbn?: string;
  cover?: string;
  published: number;
  createdBy: IUser;
  createdAt: StringDate;
  updatedBy: IUser;
  updatedAt: StringDate;
  deletedAt: StringDate;
  authors: Author[];
}

export interface IQuote {
  id: number;
  engText: string;
  origText: string;
  origLang: string;
  origTimePeriod: string;
  published: number;
  views: number;
  likes: number;
  hides: number;
  shares: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  note?: string;
  tags: ITag[];
  refs: Reference[];
  cultures: ICulture[];
  sources: ISource[];
  trans: ITranslation[];
  authors: { id: number; name: string }[];
  createdBy: { id: number; name: string };
  updatedBy: { id: number; name: string };
}

export interface UserRole {
  id: number;
  role: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface UserLike {
  id: number;
  targetId: number;
  targetType: string;
  createdAt: Date;
  updatedAt: Date;
  user: IUser;
}

export interface UserHide {
  id: number;
  targetId: number;
  targetType: string;
  createdAt: Date;
  updatedAt: Date;
  user: IUser;
}

export interface UserShare {
  targetId: number;
  targetType: string;
  createdAt: Date;
  updatedAt: Date;
  user: IUser;
}

export interface IUser {
  id: number;
  googleUserId?: string;
  appleUserId?: string;
  name: string;
  email: string;
  avatar?: string;
  status: string;
  subscription?: string;
  lastLoginAt?: Date;
  subscribedAt?: Date;
  subscriptionStatus?: string | null;
  subscriptionProductId?: string | null;
  subscriptionStore?: string | null;
  subscriptionStartAt?: Date;
  subscriptionEndAt?: Date;
  unsubscribedAt?: Date;
  mostRecentLoginCity?: string;
  mostRecentLoginCountry?: string;
  mostRecentLoginDevice?: string;
  profile: any;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  tags: ITag[];
  roles: UserRole[];
  totalLikes?: number;
  totalHides?: number;
  totalShares?: number;
  totalCulturesSubscribed?: number;
  totalTagsSubscribed?: number;
  customTrial?: {
    status: string;
    startedAt: string;
    expiresAt: string;
  } | null;
}

export interface Author {
  id: number | string;
  name: string;
  avatar?: string;
  published: number;
  createdBy: IUser;
  createdAt: StringDate;
  updatedBy: IUser;
  updatedAt: StringDate;
  deletedAt?: StringDate;
  books: Book[];
}

type StringDate = string | Date;

export interface Reference {
  id: number | string;
  text: string;
  createdAt: Date;
  published: number;
  updatedBy: IUser;
  createdBy: IUser;
  updatedAt: Date;
  deletedAt: Date;
}

export interface ITag {
  id: number | string;
  tag: string;
  type: string;
  mobileHidden: boolean;
  totalQuotes: number;
  totalSubscribers: number;
  published: number;
  createdBy: IUser;
  createdAt: Date;
  updatedBy: IUser;
  updatedAt: Date;
  deletedAt: Date;
}

export interface PreviewTag extends Omit<ITag, "published" | "updatedBy" | "createdBy"> {
  totalQuotes: number;
  totalUsers: number;
  published: string;
  updatedBy: string;
  createdBy: string;
}

// Translation
export interface ITranslation {
  id: number | string;
  text: string;
  lang: string;
  published: number;
  refs?: { id: string; text: string }[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

//cultures
export interface ICultureCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  published: number;
  totalCultures: number;
  createdBy: IUser;
  createdAt: Date;
  updatedBy: IUser;
  updatedAt: Date;
  deletedAt: Date;
}

export interface IPreviewCultureCategory extends Omit<ICultureCategory, "published" | "updatedBy" | "createdBy"> {
  published: string;
  updatedBy: string;
  createdBy: string;
}

export interface ICulture {
  id: number;
  culture: string;
  icon: string;
  totalQuotes: number;
  totalSubscribers: number;
  published: number;
  cultureCategories?: ICultureCategory[];
  createdBy: IUser;
  createdAt: Date;
  updatedBy: IUser;
  updatedAt: Date;
  deletedAt: Date;
}

export interface IPreviewCulture extends Omit<ICulture, "published"> {
  published: string | number;
}

export interface IFileUploadHandle {
  uploadFile: () => Promise<string | null>;
}

export interface IUpdateCulture {
  id: number | string;
  culture: string;
  icon?: string;
}

// Sources
export interface ISource {
  id: number;
  name: string;
  isbn: string;
  published: number;
  cover: string;
  authors: IUser[];
  createdAt: Date;
  createdBy: IUser;
  updatedAt: Date;
  updatedBy: IUser;
}

export interface IPreviewSource extends Omit<ISource, "published"> {
  published: string | number;
}

// Languages
export interface ILanguage {
  id: number;
  lang: string;
  published: number;
  createdBy: IUser;
  createdAt: Date;
  updatedBy: IUser;
  updatedAt: Date;
  deletedAt: Date;
}

export interface IPageTab {
  label: TabLabelEnum;
  value: TabKeyEnum;
  params: {
    published: 1 | 0 | -1 | -2;
  };
}

export enum TabKeyEnum {
  Published = "published",
  ReadyForReview = "readyForReview",
  Unpublished = "unpublished",
  Archived = "archived",
}

export enum TabLabelEnum {
  Published = "Published",
  ReadyForReview = "Ready for review",
  Unpublished = "Unpublished",
  Archived = "Archived",
}

export interface TableMenuItemInterface {
  label: string;
  onClick: () => void;
  visible: boolean;
  sx?: SxProps<Theme>;
}

export enum SubmitButtonTextEnum {
  ReadyForReview = "Ready for Review",
  SaveAsUnpublished = "Save as Unpublished",
}

export enum SubmitButtonEnum {
  ReadyForReview = "ReadyForReview",
  SaveAsUnpublished = "SaveAsUnpublished",
}

export type SubmitButtonType = keyof typeof SubmitButtonEnum;

export enum ModalEnum {
  Create = "Create",
  Edit = "Edit",
  Preview = "Preview",
}

export type ModalType = keyof typeof ModalEnum;

export enum ConfirmDialogEnum {
  Delete = "Delete",
  Publish = "Publish",
  Unpublish = "Unpublish",
  Save = "Save",
  Activate = "Activate",
  Deactivate = "Deactivate",
}

export type ConfirmDialogType = keyof typeof ConfirmDialogEnum;

export enum TagTypeEnum {
  MainCategory = "Main Category",
  Category = "Category",
}

export enum TagVisibilityEnum {
  Visible = "Shown to User",
  Hidden = "Hidden from User",
}

// Dashboard
export enum TimePeriodEnum {
  Last7Days = "last7days",
  LastMonth = "lastMonth",
  LastYear = "lastYear",
  Custom = "custom",
}
